import React from "react";
import lottie from "lottie-web";
import lottieApi from "lottie-api";
import PropTypes from "prop-types";
import { LOAD_ANIMATION_CONFIG } from "./LottiePlayer.constants";
import cname from "../../Style/style.module.scss";

/**
 * LottierPlayer
 *  - A wrapper over the lottie-web and lotte-api libraries
 *  - Function as a Lottie Animation file player
 */
export function LottiePlayer(props) {
  const elementRef = React.useRef();

  const [state, setState] = React.useState({ anime: null, animeApi: null });

  React.useEffect(() => {
    if (props.animationData) {
      const anime = getLottieInstance(props, elementRef.current);
      const animeApi = getLottieApiInstance(anime);
      setState({ anime, animeApi });
    }
  }, [props.animationData]);

  React.useEffect(() => {
    if (props.animationControl && state.animeApi) {
      const properties = Object.keys(props.animationControl);

      properties.forEach((property) => {
        const keyPath = state.animeApi.getKeyPath(property);
        const value = props.animationControl[property];
        state.animeApi.addValueCallback(keyPath, () => {
          return value;
        });
        state.anime.renderer.renderFrame(null);
      });
    }
  }, [props.animationControl]);

  React.useEffect(() => {
    if (state.anime) {
      state.anime.renderer.renderFrame(null);
    }
  });

  const wrapperClass = `${cname.player} ${props.className}`;

  return (
    <div style={props.style} tabIndex={props.tabIndex} className={wrapperClass}>
      <div
        className={cname.animation}
        style={props.elemStyle}
        ref={elementRef}
      />
    </div>
  );
}

LottiePlayer.propTypes = {
  /**
   * Set to play animation in loop
   */
  loop: PropTypes.bool,
  /**
   * Set to autoplay the animation
   */
  autoplay: PropTypes.bool,
  /**
   * lottie file to be displayed [Not used with path prop]
   */
  // animationData: PropTypes.object,
  /**
   * Style to set on player wrapper
   */
  style: PropTypes.object,
  /**
   * Style to set on animation element
   */
  elemStyle: PropTypes.object,
  /**
   * To control the lottie animation
   */
  animationControl: PropTypes.object,
};

LottiePlayer.defaultProps = {
  tabIndex: null,
  style: {},
  loop: false,
  className: "",
  elemStyle: {},
  autoplay: false,
  animationData: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  animationLoaded: () => {},
};

/**
 * Function to create an instance of Animation
 */
function getLottieInstance(params, container) {
  return lottie.loadAnimation({
    ...LOAD_ANIMATION_CONFIG,
    loop: params.loop,
    autoplay: params.autoplay,
    container: container,
    animationData: params.animationData,
  });
}

/**
 * Function to create an instance of LottieApi
 */
function getLottieApiInstance(animationInstance) {
  return lottieApi.createAnimationApi(animationInstance);
}
